import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { settlementAPI } from '../../services/api';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  Number(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIOD_LABELS = { daily: 'يومي', weekly: 'أسبوعي', monthly: 'شهري' };

// ── Stats cards ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color }) => (
  <div className={`bg-white rounded-2xl shadow p-5 border-r-4 ${color}`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-800">{fmt(value)} <span className="text-sm font-normal text-gray-400">SDG</span></p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

// ── Period selector ───────────────────────────────────────────────────────────

const PeriodSelector = ({ value, onChange }) => (
  <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
    {Object.entries(PERIOD_LABELS).map(([key, label]) => (
      <button
        key={key}
        onClick={() => onChange(key)}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
          value === key
            ? 'bg-white shadow text-gray-800'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// ── Date range search bar ─────────────────────────────────────────────────────

const DateRangeBar = ({ dateFrom, dateTo, onFromChange, onToChange, onSearch, onClear, hasCustom }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <label className="text-xs text-gray-400 ml-1">من</label>
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => onFromChange(e.target.value)}
        className="text-sm text-gray-700 bg-transparent focus:outline-none w-32"
      />
    </div>
    <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <label className="text-xs text-gray-400 ml-1">إلى</label>
      <input
        type="date"
        value={dateTo}
        onChange={(e) => onToChange(e.target.value)}
        className="text-sm text-gray-700 bg-transparent focus:outline-none w-32"
      />
    </div>
    <button
      onClick={onSearch}
      disabled={!dateFrom || !dateTo}
      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
      </svg>
      بحث
    </button>
    {hasCustom && (
      <button
        onClick={onClear}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 bg-gray-100 hover:bg-red-50 px-3 py-2 rounded-xl transition"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        مسح
      </button>
    )}
  </div>
);

// ── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  paid:      { label: 'مسدد ✓',           cls: 'bg-green-100 text-green-700' },
  submitted: { label: 'بانتظار التأكيد ⏳', cls: 'bg-blue-100 text-blue-700' },
  pending:   { label: 'غير مسدد',          cls: 'bg-amber-100 text-amber-700' },
  rejected:  { label: 'مرفوض',             cls: 'bg-red-100 text-red-700' },
  none:      { label: 'لا يوجد',           cls: 'bg-gray-100 text-gray-400' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.none;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>;
};

// ── Receipt viewer modal — fetches through the authenticated /api/receipts endpoint ──

const ReceiptModal = ({ url: rawUrl, companyName, onClose }) => {
  const [blobUrl,  setBlobUrl]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);

  const isPdf = rawUrl?.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    if (!rawUrl) return;
    // Migrate legacy /uploads/receipt-* paths to the authenticated endpoint
    const url = rawUrl.replace(/\/uploads\/(receipt-[^/?#]+)/, '/api/receipts/$1');
    setLoading(true);
    setError(false);
    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed');
        return res.blob();
      })
      .then((blob) => {
        setBlobUrl(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [rawUrl]); // blobUrl excluded intentionally — only used for cleanup, not as a trigger

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col max-w-2xl w-full max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-800">سند التحويل</h3>
            <p className="text-xs text-gray-400">{companyName}</p>
          </div>
          <div className="flex items-center gap-3">
            {blobUrl && (
              <a href={blobUrl} download={`receipt-${companyName}.${isPdf ? 'pdf' : 'jpg'}`}
                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg transition">
                تحميل
              </a>
            )}
            <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center min-h-[200px]">
          {loading && (
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              <span className="text-sm">جاري تحميل الإيصال…</span>
            </div>
          )}
          {error && (
            <p className="text-red-500 text-sm">تعذّر تحميل الإيصال. يرجى المحاولة مجدداً.</p>
          )}
          {!loading && !error && blobUrl && (
            isPdf
              ? <iframe src={blobUrl} title="receipt" className="w-full h-[60vh] rounded-xl border" />
              : <img src={blobUrl} alt="receipt" className="max-w-full mx-auto rounded-xl border" />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Reject modal ─────────────────────────────────────────────────────────────

const RejectModal = ({ settlement, companyName, onClose, onDone }) => {
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);

  const handleReject = async () => {
    setLoading(true);
    try {
      await settlementAPI.rejectPayment(settlement.settlementId, { note });
      toast.success('تم رفض الإيصال وإعلام الشركة');
      onDone();
      onClose();
    } catch {
      toast.error('فشل الرفض');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h3 className="font-bold text-gray-800">رفض سند الدفع</h3>
        <p className="text-sm text-gray-500">{companyName}</p>
        <div>
          <label className="block text-sm text-gray-600 mb-1">سبب الرفض <span className="text-gray-400">(اختياري)</span></label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="مثال: الحساب غير صحيح، الصورة غير واضحة..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 transition">إلغاء</button>
          <button
            onClick={handleReject}
            disabled={loading}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 rounded-xl text-sm font-semibold transition"
          >
            {loading ? '...' : 'تأكيد الرفض'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Export button ─────────────────────────────────────────────────────────────

const ExportButton = ({ companyId, companyName, periodStart, periodEnd, periodType }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await settlementAPI.exportPdf({ companyId, periodStart, periodEnd, periodType });
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `settlement_${companyName}_${periodStart}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('فشل تحميل الملف');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={loading}
      title="تصدير PDF"
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 disabled:opacity-40 transition px-2 py-1 rounded-lg hover:bg-red-50"
    >
      {loading ? (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )}
      PDF
    </button>
  );
};

// ── Booking Detail Modal ──────────────────────────────────────────────────────

const BookingDetailModal = ({ company, period, onClose }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    settlementAPI.getCompanyBookings({
      companyId:   company.companyId,
      periodStart: period.start,
      periodEnd:   period.end,
    })
      .then((res) => setBookings(res.data.data || []))
      .catch(() => toast.error('فشل تحميل الحجوزات'))
      .finally(() => setLoading(false));
  }, [company.companyId, period.start, period.end]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await settlementAPI.exportPdf({
        companyId:   company.companyId,
        periodStart: period.start,
        periodEnd:   period.end,
        periodType:  'monthly',
      });
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `settlement_${company.companyName}_${period.start}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('فشل تحميل الملف');
    } finally {
      setExporting(false);
    }
  };

  const totalRevenue    = bookings.reduce((s, b) => s + b.totalAmount, 0);
  const totalCommission = bookings.reduce((s, b) => s + b.commissionAmount, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{company.companyName}</h2>
            <p className="text-sm text-gray-400">{period.label} &nbsp;·&nbsp; {period.start} — {period.end}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exporting || loading}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
            >
              {exporting ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              تصدير PDF
            </button>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Summary strip */}
        {!loading && (
          <div className="flex gap-6 px-6 py-3 bg-blue-50 border-b text-sm">
            <span className="text-gray-600">إجمالي الحجوزات: <strong className="text-gray-800">{bookings.length}</strong></span>
            <span className="text-gray-600">إجمالي الإيرادات: <strong className="text-blue-700">{fmt(totalRevenue)} SDG</strong></span>
            <span className="text-gray-600">إجمالي العمولات: <strong className="text-green-700">{fmt(totalCommission)} SDG</strong></span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
          ) : bookings.length === 0 ? (
            <div className="py-16 text-center text-gray-400">لا توجد حجوزات في هذه الفترة</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">رقم التذكرة</th>
                  <th className="px-4 py-3 text-right font-medium">المسافرون</th>
                  <th className="px-4 py-3 text-center font-medium">الخط</th>
                  <th className="px-4 py-3 text-center font-medium">التاريخ</th>
                  <th className="px-3 py-3 text-center font-medium">المقاعد</th>
                  <th className="px-4 py-3 text-center font-medium">المبلغ</th>
                  <th className="px-4 py-3 text-center font-medium">العمولة</th>
                  <th className="px-3 py-3 text-center font-medium">المصدر</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bookings.map((b, i) => (
                  <tr key={i} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      {b.ticketNumbers?.length > 0 ? (
                        <div className="space-y-0.5">
                          {b.ticketNumbers.map((tn, ti) => (
                            <div key={ti} className="font-mono text-xs text-blue-700 font-semibold whitespace-nowrap">{tn}</div>
                          ))}
                        </div>
                      ) : (
                        <span className="font-mono text-xs text-gray-400 whitespace-nowrap">{b.bookingNumber}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      {b.passengers.length > 0 ? (
                        <div className="space-y-0.5">
                          {b.passengers.map((p, pi) => (
                            <div key={pi} className="text-xs text-gray-700 flex items-center gap-1">
                              <span className="inline-flex w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs items-center justify-center font-bold flex-shrink-0">
                                {p.seat}
                              </span>
                              <span>{p.name || '—'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-gray-600 whitespace-nowrap">{b.route || '—'}</td>
                    <td className="px-4 py-3 text-center text-xs text-gray-500 whitespace-nowrap">
                      {new Date(b.createdAt).toLocaleDateString('ar-EG')}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-600">{b.seats}</td>
                    <td className="px-4 py-3 text-center font-medium text-gray-800">{fmt(b.totalAmount)}</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-700">{fmt(b.commissionAmount)}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        b.bookingSource === 'online'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {b.bookingSource === 'online' ? 'إلكتروني' : 'مكتب'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Company Commission Tab ────────────────────────────────────────────────────

const CompanyCommissionTab = ({ periodType }) => {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [paying, setPaying]     = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [modal, setModal]       = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [rejectModal, setRejectModal]   = useState(null);

  // Date range search
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [activeRange, setActiveRange] = useState(null); // { dateFrom, dateTo } when custom search is active

  const load = useCallback((customRange) => {
    setLoading(true);
    const params = customRange
      ? { dateFrom: customRange.dateFrom, dateTo: customRange.dateTo }
      : { periodType, periods: 6 };
    settlementAPI.getOverview(params)
      .then((res) => setData(res.data.data?.rows || []))
      .catch(() => toast.error('فشل تحميل بيانات العمولات'))
      .finally(() => setLoading(false));
  }, [periodType]);

  const handleSearch = () => {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) { toast.error('تاريخ البداية يجب أن يكون قبل تاريخ النهاية'); return; }
    setExpanded(null);
    setActiveRange({ dateFrom, dateTo });
    load({ dateFrom, dateTo });
  };

  const handleClear = () => {
    setActiveRange(null);
    setDateFrom('');
    setDateTo('');
    load(null);
  };

  useEffect(() => { load(null); }, [load]);

  const handlePay = async (row, company) => {
    setPaying(`${company.companyId}__${row.period.start}`);
    try {
      await settlementAPI.markPaid({
        companyId:        company.companyId,
        direction:        'receivable',
        periodType,
        periodStart:      row.period.start,
        periodEnd:        row.period.end,
        bookingCount:     company.bookingCount,
        totalRevenue:     company.totalRevenue,
        commissionAmount: company.commissionAmount,
      });
      toast.success(`تم تسجيل تسوية ${company.companyName}`);
      load(activeRange);
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل التسوية');
    } finally {
      setPaying(null);
    }
  };

  const handleUnpay = async (company) => {
    if (!company.settlementId) return;
    if (!window.confirm('هل تريد التراجع عن التسوية؟')) return;
    try {
      await settlementAPI.revertPaid(company.settlementId);
      toast.success('تم التراجع عن التسوية');
      load(activeRange);
    } catch {
      toast.error('فشل التراجع');
    }
  };

  const handleConfirm = async (company) => {
    if (!company.settlementId) return;
    setConfirming(company.settlementId);
    try {
      await settlementAPI.confirmPayment(company.settlementId);
      toast.success(`تم تأكيد استلام دفعة ${company.companyName}`);
      load(activeRange);
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل التأكيد');
    } finally {
      setConfirming(null);
    }
  };

  return (
    <>
    {modal && (
      <BookingDetailModal
        company={modal.company}
        period={modal.period}
        onClose={() => setModal(null)}
      />
    )}
    {receiptModal && (
      <ReceiptModal
        url={receiptModal.url}
        companyName={receiptModal.companyName}
        onClose={() => setReceiptModal(null)}
      />
    )}
    {rejectModal && (
      <RejectModal
        settlement={rejectModal}
        companyName={rejectModal.companyName}
        onClose={() => setRejectModal(null)}
        onDone={() => load(activeRange)}
      />
    )}

    {/* Date range search */}
    <DateRangeBar
      dateFrom={dateFrom}
      dateTo={dateTo}
      onFromChange={setDateFrom}
      onToChange={setDateTo}
      onSearch={handleSearch}
      onClear={handleClear}
      hasCustom={!!activeRange}
    />

    {activeRange && (
      <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        نتائج البحث من <strong className="mx-1">{activeRange.dateFrom}</strong> إلى <strong className="mx-1">{activeRange.dateTo}</strong>
      </div>
    )}

    {loading ? (
      <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
    ) : data.length === 0 ? (
      <div className="py-16 text-center text-gray-400">لا توجد بيانات في هذه الفترة</div>
    ) : null}

    <div className="space-y-4">
      {data.map((row, ri) => {
        const totalCommission = row.companies.reduce((s, c) => s + c.commissionAmount, 0);
        const pendingCount    = row.companies.filter((c) => c.status === 'pending').length;
        const isOpen          = expanded === ri;

        return (
          <div key={ri} className="bg-white rounded-2xl shadow overflow-hidden">
            {/* Period header */}
            <button
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-right"
              onClick={() => setExpanded(isOpen ? null : ri)}
            >
              <div className="flex items-center gap-3">
                <span className="font-bold text-gray-800">{row.period.label}</span>
                <span className="text-xs text-gray-400">{row.period.start} — {row.period.end}</span>
                {pendingCount > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {pendingCount} غير مسدد
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-left">
                  <p className="text-xs text-gray-400">إجمالي العمولات</p>
                  <p className="font-bold text-gray-800">{fmt(totalCommission)} SDG</p>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Company rows */}
            {isOpen && (
              <div className="border-t">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-5 py-3 text-right font-medium">الشركة</th>
                      <th className="px-4 py-3 text-center font-medium">حجوزات</th>
                      <th className="px-4 py-3 text-center font-medium">إجمالي الإيرادات</th>
                      <th className="px-4 py-3 text-center font-medium">العمولة المستحقة</th>
                      <th className="px-4 py-3 text-center font-medium">الحالة</th>
                      <th className="px-4 py-3 text-center font-medium">إجراء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {row.companies.map((c) => {
                      const key = `${c.companyId}__${row.period.start}`;
                      return (
                        <tr key={c.companyId} className="hover:bg-gray-50/60">
                          <td className="px-5 py-3">
                            <button
                              onClick={() => setModal({ company: c, period: row.period })}
                              className="flex items-center gap-2 text-right hover:underline group"
                            >
                              <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {c.companyCode || c.companyName?.[0]}
                              </div>
                              <span className="font-medium text-blue-700 group-hover:text-blue-900">{c.companyName}</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{c.bookingCount}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(c.totalRevenue)}</td>
                          <td className="px-4 py-3 text-center font-semibold text-blue-700">{fmt(c.commissionAmount)}</td>
                          <td className="px-4 py-3 text-center">
                            <StatusBadge status={c.status} />
                            {c.paidAt && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(c.paidAt).toLocaleDateString('ar-EG')}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">

                              {/* submitted: view receipt + confirm + reject */}
                              {c.status === 'submitted' && c.paymentProofUrl && (
                                <button
                                  onClick={() => setReceiptModal({ url: c.paymentProofUrl, companyName: c.companyName })}
                                  className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2.5 py-1.5 rounded-lg transition font-medium flex items-center gap-1"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  الإيصال
                                </button>
                              )}
                              {c.status === 'submitted' && (
                                <button
                                  onClick={() => handleConfirm(c)}
                                  disabled={confirming === c.settlementId}
                                  className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition font-medium"
                                >
                                  {confirming === c.settlementId ? '...' : 'تأكيد'}
                                </button>
                              )}
                              {c.status === 'submitted' && (
                                <button
                                  onClick={() => setRejectModal({ ...c, companyName: c.companyName })}
                                  className="text-xs bg-red-50 hover:bg-red-100 text-red-600 px-2.5 py-1.5 rounded-lg transition font-medium"
                                >
                                  رفض
                                </button>
                              )}

                              {/* rejected: view old receipt + manual mark as paid */}
                              {c.status === 'rejected' && c.paymentProofUrl && (
                                <button
                                  onClick={() => setReceiptModal({ url: c.paymentProofUrl, companyName: c.companyName })}
                                  className="text-xs text-gray-400 hover:text-blue-600 transition"
                                >
                                  عرض السند
                                </button>
                              )}

                              {/* pending / rejected: manual mark as paid */}
                              {['pending', 'rejected'].includes(c.status) && (
                                <button
                                  onClick={() => handlePay(row, c)}
                                  disabled={paying === key}
                                  className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition font-medium"
                                >
                                  {paying === key ? '...' : 'تسجيل يدوي'}
                                </button>
                              )}

                              {/* paid: revert option */}
                              {c.status === 'paid' && (
                                <button
                                  onClick={() => handleUnpay(c)}
                                  className="text-xs text-red-400 hover:text-red-600 transition"
                                >
                                  تراجع
                                </button>
                              )}

                              {c.bookingCount > 0 && (
                                <ExportButton
                                  companyId={c.companyId}
                                  companyName={c.companyName}
                                  periodStart={row.period.start}
                                  periodEnd={row.period.end}
                                  periodType={periodType}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Period total */}
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td colSpan={3} className="px-5 py-3 text-sm font-semibold text-gray-700 text-right">المجموع</td>
                      <td className="px-4 py-3 text-center font-bold text-blue-800">{fmt(totalCommission)}</td>
                      <td colSpan={2} className="px-5 py-3 text-xs text-gray-400 text-left">
                        {row.companies.filter((c) => c.status === 'paid').length} مسدد / {row.companies.filter((c) => c.status === 'pending').length} غير مسدد
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
    </>
  );
};

// ── Agency Payout Tab ─────────────────────────────────────────────────────────

const AgencyPayoutTab = ({ periodType, onManageAgencies }) => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    settlementAPI.getAgencies({ periodType, periods: 6 })
      .then((res) => setData(res.data.data?.rows || []))
      .catch(() => toast.error('فشل تحميل بيانات الوكالات'))
      .finally(() => setLoading(false));
  }, [periodType]);

  useEffect(() => { load(); }, [load]);

  const handlePay = async (row, agency) => {
    setPaying(`${agency.companyId}__${row.period.start}`);
    try {
      await settlementAPI.markPaid({
        companyId:        agency.companyId,
        direction:        'payable',
        periodType,
        periodStart:      row.period.start,
        periodEnd:        row.period.end,
        bookingCount:     agency.bookingCount,
        totalRevenue:     agency.totalRevenue,
        commissionAmount: agency.payoutAmount,
      });
      toast.success(`تم تسجيل دفع مستحقات ${agency.companyName}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل التسجيل');
    } finally {
      setPaying(null);
    }
  };

  const handleUnpay = async (agency) => {
    if (!agency.settlementId) return;
    if (!window.confirm('هل تريد التراجع؟')) return;
    try {
      await settlementAPI.revertPaid(agency.settlementId);
      toast.success('تم التراجع');
      load();
    } catch {
      toast.error('فشل التراجع');
    }
  };

  const hasAnyAgency = data.some((row) => row.agencies?.length > 0);

  if (loading) return <div className="py-16 text-center text-gray-400">جاري التحميل...</div>;

  if (!hasAnyAgency) {
    return (
      <div className="py-16 text-center space-y-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <p className="text-gray-600 font-medium">لا توجد وكالات مسجلة</p>
          <p className="text-gray-400 text-sm mt-1">يمكنك تحديد أي شركة كـ "وكالة حجز" من صفحة الشركات.</p>
        </div>
        <button
          onClick={onManageAgencies}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-sm font-medium transition"
        >
          إدارة الشركات
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.filter((row) => row.agencies?.length > 0).map((row, ri) => {
        const totalPayout = row.agencies.reduce((s, a) => s + a.payoutAmount, 0);
        const pendingCount = row.agencies.filter((a) => a.status === 'pending').length;
        const isOpen = expanded === ri;

        return (
          <div key={ri} className="bg-white rounded-2xl shadow overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-right"
              onClick={() => setExpanded(isOpen ? null : ri)}
            >
              <div className="flex items-center gap-3">
                <span className="font-bold text-gray-800">{row.period.label}</span>
                <span className="text-xs text-gray-400">{row.period.start} — {row.period.end}</span>
                {pendingCount > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {pendingCount} غير مدفوع
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-left">
                  <p className="text-xs text-gray-400">إجمالي المستحقات</p>
                  <p className="font-bold text-purple-700">{fmt(totalPayout)} SDG</p>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="border-t">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-5 py-3 text-right font-medium">الوكالة</th>
                      <th className="px-4 py-3 text-center font-medium">حجوزات</th>
                      <th className="px-4 py-3 text-center font-medium">قيمة الحجوزات</th>
                      <th className="px-4 py-3 text-center font-medium">نسبة العمولة</th>
                      <th className="px-4 py-3 text-center font-medium">المستحق للوكالة</th>
                      <th className="px-4 py-3 text-center font-medium">الحالة</th>
                      <th className="px-4 py-3 text-center font-medium">إجراء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {row.agencies.map((a) => {
                      const key = `${a.companyId}__${row.period.start}`;
                      return (
                        <tr key={a.companyId} className="hover:bg-gray-50/60">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {a.companyCode || a.companyName?.[0]}
                              </div>
                              <span className="font-medium text-gray-800">{a.companyName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{a.bookingCount}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{fmt(a.totalRevenue)}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{a.agencyCommissionRate}%</td>
                          <td className="px-4 py-3 text-center font-semibold text-purple-700">{fmt(a.payoutAmount)}</td>
                          <td className="px-4 py-3 text-center">
                            <StatusBadge status={a.status} />
                            {a.paidAt && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(a.paidAt).toLocaleDateString('ar-EG')}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {a.status === 'pending' && (
                              <button
                                onClick={() => handlePay(row, a)}
                                disabled={paying === key}
                                className="text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition font-medium"
                              >
                                {paying === key ? '...' : 'تسجيل كمدفوع'}
                              </button>
                            )}
                            {a.status === 'paid' && (
                              <button
                                onClick={() => handleUnpay(a)}
                                className="text-xs text-red-400 hover:text-red-600 transition"
                              >
                                تراجع
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td colSpan={4} className="px-5 py-3 text-sm font-semibold text-gray-700 text-right">المجموع</td>
                      <td className="px-4 py-3 text-center font-bold text-purple-800">{fmt(totalPayout)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Transport Provider Commission Tab (مشاوير + نقل عام) ─────────────────────
// Read-only for now — no mark-paid/settle action, since Settlement has no
// providerId FK yet (see backend/src/controllers/settlementController.js's
// getTransportOverview comment). Just the totals that were previously
// nowhere in the admin portal.
const PROVIDER_TYPE_LABELS = {
  individual_driver: 'سائق فردي', vehicle_owner: 'صاحب مركبة', transport_office: 'مكتب نقل',
  transport_company: 'شركة نقل', logistics_company: 'شركة لوجستية', fleet_owner: 'صاحب أسطول',
};

const TransportCommissionTab = ({ periodType }) => {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [activeRange, setActiveRange] = useState(null);

  const load = useCallback((customRange) => {
    setLoading(true);
    const params = customRange
      ? { dateFrom: customRange.dateFrom, dateTo: customRange.dateTo }
      : { periodType, periods: 6 };
    settlementAPI.getTransportOverview(params)
      .then((res) => setData(res.data.data?.rows || []))
      .catch(() => toast.error('فشل تحميل عمولات مزودي النقل'))
      .finally(() => setLoading(false));
  }, [periodType]);

  const handleSearch = () => {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) { toast.error('تاريخ البداية يجب أن يكون قبل تاريخ النهاية'); return; }
    setExpanded(null);
    setActiveRange({ dateFrom, dateTo });
    load({ dateFrom, dateTo });
  };

  const handleClear = () => {
    setActiveRange(null);
    setDateFrom('');
    setDateTo('');
    load(null);
  };

  useEffect(() => { load(null); }, [load]);

  return (
    <>
      <DateRangeBar
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onSearch={handleSearch}
        onClear={handleClear}
        hasCustom={!!activeRange}
      />

      {activeRange && (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2 mt-3">
          نتائج البحث من <strong className="mx-1">{activeRange.dateFrom}</strong> إلى <strong className="mx-1">{activeRange.dateTo}</strong>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
      ) : data.every((row) => row.providers.length === 0) ? (
        <div className="py-16 text-center text-gray-400">لا توجد طلبات مشاوير أو نقل عام مكتملة في هذه الفترة</div>
      ) : null}

      <div className="space-y-4 mt-4">
        {data.map((row, ri) => {
          if (row.providers.length === 0) return null;
          const totalCommission = row.providers.reduce((s, p) => s + p.totalCommission, 0);
          const isOpen = expanded === ri;

          return (
            <div key={ri} className="bg-white rounded-2xl shadow overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-right"
                onClick={() => setExpanded(isOpen ? null : ri)}
              >
                <div className="flex items-center gap-3">
                  <span className="font-bold text-gray-800">{row.period.label}</span>
                  <span className="text-xs text-gray-400">{row.period.start} — {row.period.end}</span>
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {row.providers.length} مزود
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-left">
                    <p className="text-xs text-gray-400">إجمالي العمولات</p>
                    <p className="font-bold text-gray-800">{fmt(totalCommission)} SDG</p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {isOpen && (
                <div className="border-t overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-500">
                      <tr>
                        <th className="px-5 py-3 text-right font-medium">المزود</th>
                        <th className="px-3 py-3 text-center font-medium">طلبات مشاوير</th>
                        <th className="px-3 py-3 text-center font-medium">عمولة مشاوير</th>
                        <th className="px-3 py-3 text-center font-medium">طلبات نقل عام</th>
                        <th className="px-3 py-3 text-center font-medium">عمولة نقل عام</th>
                        <th className="px-4 py-3 text-center font-medium">الإجمالي</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {row.providers.map((p) => (
                        <tr key={p.providerId} className="hover:bg-gray-50/60">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {p.providerName?.[0]}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{p.providerName}</p>
                                <p className="text-xs text-gray-400">{PROVIDER_TYPE_LABELS[p.providerType] || p.providerType}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center text-gray-600">{p.rides.orderCount || '—'}</td>
                          <td className="px-3 py-3 text-center font-semibold text-blue-700">{p.rides.commissionAmount ? fmt(p.rides.commissionAmount) : '—'}</td>
                          <td className="px-3 py-3 text-center text-gray-600">{p.cargo.orderCount || '—'}</td>
                          <td className="px-3 py-3 text-center font-semibold text-purple-700">{p.cargo.commissionAmount ? fmt(p.cargo.commissionAmount) : '—'}</td>
                          <td className="px-4 py-3 text-center font-bold text-emerald-700">{fmt(p.totalCommission)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td colSpan={5} className="px-5 py-3 text-sm font-semibold text-gray-700 text-right">المجموع</td>
                        <td className="px-4 py-3 text-center font-bold text-emerald-800">{fmt(totalCommission)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const SuperSettlementPage = () => {
  const [tab, setTab]             = useState('companies');
  const [periodType, setPeriodType] = useState('monthly');
  const [stats, setStats]         = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = () => {
    setStatsLoading(true);
    settlementAPI.getStats()
      .then((res) => setStats(res.data.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  };

  useEffect(() => { loadStats(); }, []);

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">التسويات المالية</h1>
        <p className="text-sm text-gray-500 mt-1">
          متابعة العمولات المستحقة على الشركات وتسويتها، والمبالغ المستحقة لوكالات الحجز.
        </p>
      </div>

      {/* Stats strip */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="عمولات غير مسددة (شركات)"
            value={stats.receivable.pending}
            sub="مستحقة للمنصة"
            color="border-amber-400"
          />
          <StatCard
            label="عمولات مسددة هذا الشهر"
            value={stats.receivable.paidThisMonth}
            sub="تم تحصيلها"
            color="border-green-400"
          />
          <StatCard
            label="مستحقات وكالات غير مدفوعة"
            value={stats.payable.pending}
            sub="مستحقة للوكالات"
            color="border-purple-400"
          />
          <StatCard
            label="إجمالي عمولات هذا الشهر"
            value={stats.totalCommissionThisMonth}
            sub="من الحجوزات المؤكدة"
            color="border-blue-400"
          />
        </div>
      )}

      {/* Tabs + period selector */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setTab('companies')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'companies'
                ? 'bg-white shadow text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            عمولات الشركات
          </button>
          <button
            onClick={() => setTab('agencies')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'agencies'
                ? 'bg-white shadow text-purple-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            مستحقات الوكالات
          </button>
          <button
            onClick={() => setTab('transport')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'transport'
                ? 'bg-white shadow text-emerald-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            عمولات مزودي النقل
          </button>
        </div>
        <PeriodSelector value={periodType} onChange={setPeriodType} />
      </div>

      {/* Tab content */}
      {tab === 'companies' && <CompanyCommissionTab periodType={periodType} />}
      {tab === 'agencies' && (
        <AgencyPayoutTab
          periodType={periodType}
          onManageAgencies={() => window.location.href = '/admin/super/companies'}
        />
      )}
      {tab === 'transport' && <TransportCommissionTab periodType={periodType} />}
    </div>
  );
};

export default SuperSettlementPage;
